/**
 * Persistência do agendador.
 *
 * - localStorage para a CONFIG (lista de agendamentos + flags globais).
 * - localStorage para HISTÓRICO de execuções (rolling máx 200).
 * - IndexedDB para o FileSystemDirectoryHandle (handle não é serializável).
 *
 * Schema versionado em `versao` (atual: 1) para permitir migrações futuras.
 *
 * MT-4.7: config + histórico são dados operacionais persistentes —
 * gravados via tenantStorage (chave prefixada pelo tenant corrente) para
 * agendamentos de um tenant não vazarem para outro login no mesmo
 * navegador. Leitura migra a chave antiga automaticamente. Os iframes
 * ocultos do executor herdam a mesma sessão da aba principal, logo
 * resolvem o mesmo prefixo.
 */
import {
  CHAVES_DURAVEIS,
  chaveTenant,
  codigoTenantCorrente,
  getItemTenant,
  removeItemTenant,
  setItemTenant,
  temTenantCorrente,
} from "../tenantStorage";

// As chaves vêm da allowlist de tenantStorage — é ela que decide o que
// sobrevive ao logout, e duplicar o literal aqui deixaria as duas listas
// divergirem no primeiro rename.
const [CONFIG_KEY, HISTORICO_KEY] = CHAVES_DURAVEIS;
const HISTORICO_MAX = 200;

const HANDLE_DB_NAME = "dash-pcp-agendamento";
const HANDLE_STORE_NAME = "handles";
// A pasta de destino é por TENANT: a chave única antiga fazia o primeiro
// agendamento do cliente B gravar dentro da pasta escolhida pelo cliente A
// (o handle sobrevive ao logout, e no SPA a permissão continua granted).
const HANDLE_KEY_LEGADO = "output-dir";
const chaveHandle = () => `output-dir:${codigoTenantCorrente()}`;

// ---------- Config (lista de agendamentos) ----------

/**
 * A chave de localStorage alterada por OUTRA aba é do agendador?
 *
 * O evento `storage` entrega a chave já prefixada pelo tenant; comparar com o
 * literal cru daria sempre falso.
 */
export function ehChaveDoAgendador(chave) {
  if (!chave) return false;
  return CHAVES_DURAVEIS.some((k) => chave === chaveTenant(k));
}

export function lerConfig() {
  try {
    const raw = getItemTenant(CONFIG_KEY);
    if (!raw) return configPadrao();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return configPadrao();
    return {
      versao: 1,
      pausarTodos: Boolean(parsed.pausarTodos),
      agendamentos: Array.isArray(parsed.agendamentos) ? parsed.agendamentos : [],
    };
  } catch {
    return configPadrao();
  }
}

export function salvarConfig(config) {
  try {
    const toSave = {
      versao: 1,
      pausarTodos: Boolean(config?.pausarTodos),
      agendamentos: Array.isArray(config?.agendamentos) ? config.agendamentos : [],
    };
    setItemTenant(CONFIG_KEY, JSON.stringify(toSave));
    return toSave;
  } catch (e) {
    console.error("[agendamento] Falha ao salvar config:", e);
    return null;
  }
}

function configPadrao() {
  return { versao: 1, pausarTodos: false, agendamentos: [] };
}

// ---------- Histórico ----------

export function lerHistorico() {
  try {
    const raw = getItemTenant(HISTORICO_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function registrarExecucao(entry) {
  try {
    const atual = lerHistorico();
    const novo = [entry, ...atual].slice(0, HISTORICO_MAX);
    setItemTenant(HISTORICO_KEY, JSON.stringify(novo));
    return novo;
  } catch (e) {
    console.error("[agendamento] Falha ao registrar execução:", e);
    return null;
  }
}

export function limparHistorico() {
  removeItemTenant(HISTORICO_KEY);
}

// ---------- IndexedDB: FileSystemDirectoryHandle ----------

function abrirDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(HANDLE_DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(HANDLE_STORE_NAME)) {
        db.createObjectStore(HANDLE_STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function salvarDirectoryHandle(handle) {
  if (!handle) return;
  // try/catch: uma falha aqui (modo privado, quota) descartava em silêncio a
  // pasta que o usuário acabou de escolher — a tela dizia "selecionada" e no
  // F5 seguinte ela sumia.
  try {
    const db = await abrirDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(HANDLE_STORE_NAME, "readwrite");
      tx.objectStore(HANDLE_STORE_NAME).put(handle, chaveHandle());
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (e) {
    console.error("[agendamento] Falha ao salvar handle:", e);
    throw e;
  }
}

export async function lerDirectoryHandle() {
  try {
    const db = await abrirDB();
    const handle = await new Promise((resolve, reject) => {
      const tx = db.transaction(HANDLE_STORE_NAME, "readonly");
      const req = tx.objectStore(HANDLE_STORE_NAME).get(chaveHandle());
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    if (handle) {
      db.close();
      return handle;
    }
    // Migração one-shot: quem já tinha pasta escolhida antes da chave por
    // tenant não precisa escolher de novo. Só com tenant resolvido — senão
    // adotaríamos a pasta de outro cliente.
    const legado = temTenantCorrente()
      ? await new Promise((resolve) => {
          const tx = db.transaction(HANDLE_STORE_NAME, "readonly");
          const req = tx.objectStore(HANDLE_STORE_NAME).get(HANDLE_KEY_LEGADO);
          req.onsuccess = () => resolve(req.result || null);
          req.onerror = () => resolve(null);
        })
      : null;
    db.close();
    if (legado) await salvarDirectoryHandle(legado);
    return legado;
  } catch (e) {
    console.error("[agendamento] Falha ao ler handle:", e);
    return null;
  }
}

export async function removerDirectoryHandle() {
  try {
    const db = await abrirDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(HANDLE_STORE_NAME, "readwrite");
      const store = tx.objectStore(HANDLE_STORE_NAME);
      store.delete(chaveHandle());
      store.delete(HANDLE_KEY_LEGADO);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (e) {
    console.error("[agendamento] Falha ao remover handle:", e);
  }
}
